import { PolicyLayout, PolicySection } from "@/components/PolicyLayout";

export default function Privacy() {
  return (
    <PolicyLayout
      testId="privacy-page"
      seoTitle="Privacy Policy — Foodie Crave"
      seoDescription="How Foodie Crave collects, uses, and protects your information when you discover restaurants, save favorites, and plan meals with friends."
      seoPath="/privacy"
      title="Privacy Policy"
      updated="October 20, 2026"
    >
      <PolicySection title="1. Overview">
        <p>
          Foodie Crave ("we", "our", or "the app") helps friends discover restaurants, save favorite
          places, invite each other to meals, and vote on where to eat. This Privacy Policy explains
          what information we collect, how we use it, and the choices you have. By using Foodie
          Crave, you agree to the practices described here.
        </p>
      </PolicySection>

      <PolicySection title="2. Information We Collect">
        <p>
          <span className="font-semibold text-crave-cream">Account information.</span> When you sign
          up, we collect your name, email address, and a hashed version of your password (we never
          see or store your actual password).
        </p>
        <p>
          <span className="font-semibold text-crave-cream">Content you create.</span> Saved
          restaurants, favorites lists, meal invites, votes, reviews, and any notes you add to
          places.
        </p>
        <p>
          <span className="font-semibold text-crave-cream">Usage data.</span> How you interact with
          the app, device type, operating system, device identifiers, and whether you've enabled
          biometric unlock on your device (only a Yes/No flag — no biometric data ever leaves your
          device).
        </p>
        <p>
          <span className="font-semibold text-crave-cream">Location data.</span> Approximate location
          (when you grant permission) so we can suggest restaurants near you. Precise location only
          when you tap "Use my location". You can revoke location permission at any time in your
          device settings.
        </p>
        <p>
          <span className="font-semibold text-crave-cream">Payment and subscription data.</span> Your
          subscription plan, trial status, and renewal dates. Actual payment details (card numbers,
          billing addresses) are processed by Apple App Store or Google Play — we never see them.
        </p>
      </PolicySection>

      <PolicySection title="3. How We Use Your Information">
        <p>We use your information to:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Provide and improve the app's core features — discovery, favorites, invites, and Group Vote</li>
          <li>Personalize restaurant suggestions</li>
          <li>Send service-related emails and notifications such as invite updates, vote results, password resets, and trial reminders</li>
          <li>Process your subscription via Apple App Store or Google Play billing</li>
          <li>Respond to support requests</li>
          <li>Keep the app secure and prevent abuse</li>
          <li>Comply with legal obligations</li>
        </ul>
        <p>
          <span className="font-semibold text-crave-cream">
            We do not sell your personal information. We do not share it for cross-context
            behavioral advertising.
          </span>
        </p>
      </PolicySection>

      <PolicySection title="4. How We Share Information">
        <p>
          <span className="font-semibold text-crave-cream">With your friends.</span> When you send
          an invite or join a vote, friends in that plan can see your name and votes.
        </p>
        <p>
          <span className="font-semibold text-crave-cream">
            With service providers we rely on to run Foodie Crave:
          </span>
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li><span className="font-semibold text-crave-cream">Google Places &amp; Maps</span> — restaurant discovery and geocoding</li>
          <li><span className="font-semibold text-crave-cream">MongoDB</span> — secure storage of your account and app data</li>
          <li><span className="font-semibold text-crave-cream">RevenueCat</span> — subscription management (actual payments handled by Apple App Store or Google Play)</li>
          <li><span className="font-semibold text-crave-cream">Resend</span> — transactional emails (password resets, trial reminders, dining invites)</li>
          <li><span className="font-semibold text-crave-cream">Apple Push Notification Service / Firebase Cloud Messaging</span> — delivery of push notifications if you opt in</li>
        </ul>
        <p>All service providers are bound by confidentiality obligations.</p>
        <p>
          <span className="font-semibold text-crave-cream">For legal reasons.</span> If required by
          law, regulation, or valid legal process, or to protect the rights and safety of our users.
        </p>
      </PolicySection>

      <PolicySection title="5. Data Retention">
        <p>
          We keep your information for as long as your account is active. You can delete your
          account at any time from the app's settings, after which we delete or anonymize your
          personal data within <span className="font-semibold text-crave-cream">30 days</span>,
          except where retention is required by law. Email delivery logs are retained for 90 days.
        </p>
      </PolicySection>

      <PolicySection title="6. Security">
        <p>
          We use industry-standard safeguards, including HTTPS/TLS encryption in transit, encryption
          at rest, bcrypt-hashed passwords, and strict access controls. Payment details never touch
          our servers. No method of transmission or storage is completely secure, so we encourage
          you to use a strong, unique password.
        </p>
      </PolicySection>

      <PolicySection title="7. Your Rights and Choices">
        <p>
          Depending on where you live, you may have the right to access, correct, export, or delete
          your personal data, and to object to or restrict certain processing. You can exercise
          these rights from the app's settings or by contacting us at the address below.
        </p>
        <p>
          <span className="font-semibold text-crave-cream">EU/UK (GDPR):</span> Our legal basis for
          processing is primarily contract (to provide the service), consent (location and
          notifications), and legitimate interests (safety and abuse prevention). You may lodge a
          complaint with your local Data Protection Authority.
        </p>
        <p>
          <span className="font-semibold text-crave-cream">California (CCPA/CPRA):</span> You have
          the right to know, delete, correct, and opt out of sale/share. We do not sell or share
          personal information for cross-context behavioral advertising.
        </p>
      </PolicySection>

      <PolicySection title="8. Children's Privacy">
        <p>
          Foodie Crave is not directed at children under 13, and we do not knowingly collect
          personal information from them. If you believe a child has provided us information, please
          contact us so we can remove it.
        </p>
      </PolicySection>

      <PolicySection title="9. International Users">
        <p>
          Your information may be stored and processed in the United States or other countries where
          we or our service providers operate. By using Foodie Crave, you consent to this transfer.
        </p>
      </PolicySection>

      <PolicySection title="10. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. If we make material changes, we will
          notify you in the app or by email at least 7 days before they take effect. The "Last
          updated" date above reflects the latest revision.
        </p>
      </PolicySection>

      <PolicySection title="11. Contact Us">
        <p>
          Questions about this policy or your data? Email us at{" "}
          <a
            href="mailto:support@foodiecrave.app"
            className="font-medium text-crave-gold transition-colors duration-300 hover:text-crave-cream"
          >
            support@foodiecrave.app
          </a>
          .
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}
